import type { DerivedSurface } from "@/types/look-system";
import {
  contrast,
  derivedButton,
  hexLuminance,
  isColour,
  isName,
  record,
} from "@/utils/colour-values";

type Vars = Record<string, string>;

export type EngineRole =
  | "brand"
  | "accent"
  | "background"
  | "text"
  | "band"
  | "soft";

export interface GlobalColour {
  role: string;
  follows?: EngineRole;
  followsGlobal?: string;
  readableOn?: string;
  readableAs?: "muted";
  otherScheme?: "adapts";
  legacy?: string[];
}

/**
 * The CMS's `GLOBAL_COLOURS` (reweb-cms/look-system/colour/globals.ts) without its copy, in its order;
 * tests/look-global-colours.spec.ts keeps the two equal.
 */
export const GLOBAL_COLOURS: GlobalColour[] = [
  { role: "heading" },
  { role: "link" },
  { role: "eyebrow" },
  { role: "muted" },
  { role: "border" },
  { role: "card" },
  {
    role: "card-text",
    readableOn: "card",
    legacy: ["--card-text-color", "--testimony-text-color"],
  },
  { role: "button", follows: "brand" },
  { role: "button-secondary", follows: "text" },
  { role: "header", follows: "background", legacy: ["--header-bg-color"] },
  {
    role: "header-text",
    readableOn: "header",
    legacy: ["--header-text-color", "--header-visible-text-color"],
  },
  {
    role: "menu-active",
    follows: "brand",
    legacy: ["--header-active-text-color"],
  },
  { role: "menu-panel", legacy: ["--menu-bg-color"] },
  {
    role: "menu-text",
    readableOn: "menu-panel",
    legacy: ["--menu-text-color"],
  },
  {
    role: "tabs",
    follows: "background",
    otherScheme: "adapts",
    legacy: ["--tabs-bg-color"],
  },
  {
    role: "tabs-text",
    readableOn: "tabs",
    readableAs: "muted",
    otherScheme: "adapts",
    legacy: ["--tabs-text-color"],
  },
  {
    role: "tabs-active",
    followsGlobal: "card",
    otherScheme: "adapts",
    legacy: ["--tabs-active-bg-color"],
  },
  {
    role: "tabs-active-text",
    readableOn: "tabs-active",
    otherScheme: "adapts",
    legacy: ["--tabs-active-text-color"],
  },
  { role: "footer", follows: "band", legacy: ["--footer-bg-color"] },
  {
    role: "footer-text",
    readableOn: "footer",
    legacy: ["--footer-text-color"],
  },
  { role: "field", legacy: ["--input-bg-color", "--select-option-bg-color"] },
  {
    role: "field-text",
    readableOn: "field",
    legacy: ["--input-text-color", "--select-option-text-color"],
  },
  {
    role: "form-button",
    followsGlobal: "button",
    legacy: ["--form-button-bg-color"],
  },
  {
    role: "choice",
    follows: "text",
    legacy: ["--checkbox-bg-color", "--calendar-bg-color"],
  },
  { role: "label", follows: "brand", legacy: ["--label-bg-color"] },
  {
    role: "label-text",
    readableOn: "label",
    legacy: ["--label-text-color"],
  },
  {
    role: "search-button",
    followsGlobal: "button",
    legacy: ["--search-button-bg-color"],
  },
  { role: "map-pin", follows: "brand", legacy: ["--map-pin-color"] },
  { role: "submenu", legacy: ["--casedetails-submenu-bg-color"] },
  { role: "article-heading", legacy: ["--blog-base-heading-color"] },
];

/** One scheme's colour choices, already checked, as the page rule and every background read them. */
export interface SchemeColours {
  /**
   * Each palette key's colour in this scheme: `var(--re-p-<key>)` where that variable holds this scheme's version
   * (`paletteSchemes` in utils/colour-versions.ts), else the version `derived` paints the key's background with,
   * with `held`, the colour the variable holds.
   */
  palette: Record<string, { css: string; hex: string; held?: string }>;
  redirects: Record<string, unknown>;
  globals: Record<string, unknown>;
  buttons: Record<string, unknown>;
  /** `derived[<scheme>]`: the palette keys' surfaces and `page`. */
  surfaces: Record<string, DerivedSurface>;
  /** `derived.roles`: the palette key each engine role uses. */
  roles: Record<string, unknown>;
  levels: { text: number; nonText: number };
  /** Whether this is the website's base scheme, where every stored colour is kept exactly. */
  base: boolean;
}

export const NO_COLOURS: SchemeColours = {
  palette: {},
  redirects: {},
  globals: {},
  buttons: {},
  surfaces: {},
  roles: {},
  levels: { text: 4.5, nonText: 3 },
  base: true,
};

interface Paint {
  css: string;
  hex: string;
  held?: string;
  key?: string;
}

const bestOn = (fill: string, inks: string[]) =>
  inks.reduce((best, ink) =>
    contrast(ink, fill) > contrast(best, fill) ? ink : best,
  );

function paletteKey(colours: SchemeColours, ref: string): string | undefined {
  const seen = new Set<string>();
  for (
    let current: unknown = ref;
    isName(current) && !seen.has(current);
    current = colours.redirects[current]
  ) {
    if (current in colours.palette) return current;
    seen.add(current);
  }
  return undefined;
}

function paletteRef(colours: SchemeColours, ref: unknown): Paint | undefined {
  const key = isName(ref) ? paletteKey(colours, ref) : undefined;
  return key ? { ...colours.palette[key], key } : undefined;
}

/** `var(--re-p-<ref>)` where that variable holds this scheme's version of the colour, else that version. */
export function paletteRefCss(colours: SchemeColours, ref: string): string {
  const key = paletteKey(colours, ref);
  const own = key && colours.palette[key];
  return own && own.css !== `var(--re-p-${key})`
    ? own.css
    : `var(--re-p-${ref})`;
}

function paint(colours: SchemeColours, value: unknown): Paint | undefined {
  const { ref, hex } = record(value);
  if (isName(ref)) return paletteRef(colours, ref);
  return isColour(hex) ? { css: hex, hex } : undefined;
}

/** `derived.roles` names the key; before the CMS stored it, only a role chosen as a palette colour is known. */
const rolePaint = (colours: SchemeColours, role: EngineRole) =>
  paletteRef(colours, colours.roles[role]) ??
  paletteRef(colours, record(colours.globals[role]).ref);

/** A palette colour's own background text, else the surface's text or background, whichever reads better on it. */
const readableOn = (
  colours: SchemeColours,
  fill: Paint,
  surface: DerivedSurface,
) =>
  (fill.key && colours.surfaces[fill.key]?.text) ||
  bestOn(fill.hex, [surface.text, surface.bg]);

export const globalColour = (role: string) =>
  GLOBAL_COLOURS.find((colour) => colour.role === role);

/** The fill a `readableOn` role paints in this scheme: its chosen colour, the role it follows, else the bridge's. */
function fillOf(colours: SchemeColours, role: string, bridge: Vars) {
  const value = record(colours.globals[role]);
  const colour = globalColour(role);
  const follows = value.auto === true ? undefined : colour?.follows;
  const bridged = colour?.legacy && bridge[colour.legacy[0].slice(2)];
  return (
    storedPaint(colours, role, bridge)?.hex ??
    (follows && rolePaint(colours, follows)?.hex) ??
    (isColour(bridged) ? bridged : undefined)
  );
}

/**
 * A chosen global's CSS in this scheme, and the colour it paints where the palette has it. A text colour whose palette
 * colour has no version of its own here takes the version its background is painted with only where that reads on the
 * fill under it; where the colour as stored reads better it keeps that, so text chosen as the same light colour as its
 * fill stays readable in dark.
 */
function chosenPaint(
  colours: SchemeColours,
  role: string,
  ref: string,
  bridge: Vars,
): { css: string; hex?: string } {
  const css = paletteRefCss(colours, ref);
  const chosen = paletteRef(colours, ref);
  const on = globalColour(role)?.readableOn;
  const fill = chosen?.held && on ? fillOf(colours, on, bridge) : undefined;
  if (!chosen?.held || !fill) return { css, hex: chosen?.hex };
  const version = contrast(chosen.hex, fill);
  return version < colours.levels.text && contrast(chosen.held, fill) > version
    ? { css: `var(--re-p-${ref})`, hex: chosen.held }
    : { css, hex: chosen.hex };
}

export function chosenGlobalCss(
  colours: SchemeColours,
  role: string,
  ref: string,
  bridge: Vars,
): string {
  return chosenPaint(colours, role, ref, bridge).css;
}

/**
 * The stored colour of `role` as this scheme paints it. A role that adapts (`otherScheme`) keeps it exactly in the base
 * scheme only: in the other one a fill's own colour, which has no version there, and a text that does not read on its
 * fill there count as unset, so a conversion's exact light colours give way in dark.
 */
function storedPaint(
  colours: SchemeColours,
  role: string,
  bridge: Vars,
): Paint | undefined {
  const value = record(colours.globals[role]);
  const stored = paint(colours, value);
  const colour = globalColour(role);
  if (!stored || colours.base || colour?.otherScheme !== "adapts")
    return stored;
  if (!colour.readableOn) return stored.key ? stored : undefined;
  const fill = fillOf(colours, colour.readableOn, bridge);
  const ink = isName(value.ref)
    ? chosenPaint(colours, role, value.ref, bridge).hex
    : stored.hex;
  return fill && ink && contrast(ink, fill) < colours.levels.text
    ? undefined
    : stored;
}

/**
 * The colour an unset `readableOn` text role takes on `role`, or while `role` is unset on the global it follows
 * (`followsGlobal`): a palette colour's derived text, or its muted text for `part` »muted«; undefined where that colour
 * is the bridge's.
 */
function readableText(
  colours: SchemeColours,
  role: string,
  page: DerivedSurface,
  bridge: Vars,
  part: "text" | "muted" = "text",
): string | undefined {
  const value = record(colours.globals[role]);
  const own = storedPaint(colours, role, bridge);
  if (own)
    return own.key
      ? colours.surfaces[own.key]?.[part]
      : bestOn(own.hex, [page.text, page.bg]);
  if (value.auto === true) return undefined;
  const colour = globalColour(role);
  if (colour?.followsGlobal)
    return readableText(colours, colour.followsGlobal, page, bridge, part);
  const followed = colour?.follows && rolePaint(colours, colour.follows);
  return followed?.key ? colours.surfaces[followed.key]?.[part] : undefined;
}

function namedValue(
  colours: SchemeColours,
  colour: GlobalColour,
  page: DerivedSurface,
  bridge: Vars,
) {
  const value = record(colours.globals[colour.role]);
  if (storedPaint(colours, colour.role, bridge))
    return `var(--re-${colour.role})`;
  if (colour.follows)
    return value.auto === true
      ? undefined
      : rolePaint(colours, colour.follows)?.css;
  return colour.readableOn
    ? automaticText(colours, colour, colour.readableOn, page, bridge)
    : undefined;
}

/**
 * An unset `readableOn` role's colour on `on`. In the scheme that is not the base a role that adapts must read on its
 * fill as painted there: a palette colour's own version can differ from the derived background its text was made for,
 * so a text below the level there gives way to the fill's full text, else to whichever colour reads best.
 */
function automaticText(
  colours: SchemeColours,
  colour: GlobalColour,
  on: string,
  page: DerivedSurface,
  bridge: Vars,
) {
  const text = readableText(colours, on, page, bridge, colour.readableAs);
  if (!text || colours.base || colour.otherScheme !== "adapts") return text;
  const fill = fillOf(colours, on, bridge);
  if (!isColour(fill)) return text;
  const reads = (ink: string | undefined): ink is string =>
    isColour(ink) && contrast(ink, fill) >= colours.levels.text;
  if (reads(text)) return text;
  const full = readableText(colours, on, page, bridge);
  if (reads(full)) return full;
  return bestOn(fill, [
    text,
    ...(isColour(full) ? [full] : []),
    page.text,
    page.bg,
  ]);
}

/**
 * The older variables of each global colour with a `legacy` list, for a scheme's page rule after `bridge`, the
 * page's bridged variables; a role left to the bridge is absent.
 */
export function namedGlobalVars(colours: SchemeColours, bridge: Vars): Vars {
  const page = colours.surfaces.page;
  const vars: Vars = {};
  if (!page) return vars;
  for (const colour of GLOBAL_COLOURS) {
    const value = colour.legacy && namedValue(colours, colour, page, bridge);
    if (value) for (const name of colour.legacy!) vars[name.slice(2)] = value;
  }
  return vars;
}

/**
 * `--re-<role>` of each stored global that gives way in this scheme (storedPaint), as its older variables paint it
 * there: Egen CSS reads `var(--re-<role>)`, which would otherwise keep the stored colour the role no longer shows.
 */
export function givenWayRoleVars(colours: SchemeColours, bridge: Vars): Vars {
  const page = colours.surfaces.page;
  const vars: Vars = {};
  if (!page) return vars;
  for (const colour of GLOBAL_COLOURS) {
    const stored = paint(colours, colours.globals[colour.role]);
    if (!stored || storedPaint(colours, colour.role, bridge)) continue;
    const painted =
      namedValue(colours, colour, page, bridge) ??
      (colour.legacy && bridge[colour.legacy[0].slice(2)]);
    if (painted) vars[`re-${colour.role}`] = painted;
  }
  return vars;
}

const STEP = 8;

/** A hover or press tone of `css`, `steps` steps lighter or darker, mixed in the browser. */
export const toneOf = (css: string, lighter: boolean, steps: number) =>
  `color-mix(in oklab, ${css} ${100 - STEP * steps}%, ${lighter ? "#fff" : "#000"})`;

/** Away from the text, as the CMS's engine steps a button's states, unless the fill has no room left that way. */
function lighterAway(fill: string, text: string) {
  const [lf, lt] = [hexLuminance(fill) ?? 0, hexLuminance(text) ?? 0];
  return lf >= lt ? lf < 0.8 : lf < 0.01;
}

interface State {
  bg?: Paint;
  fg?: Paint;
}

/**
 * An exact state colour off the page shows only where it stands out from the background, as the primary button's
 * does in `derived`; on the page it is kept as chosen. A text that sits on the background is held to the text level
 * there; a button's own text is kept as chosen.
 */
function exactState(
  colours: SchemeColours,
  value: unknown,
  surface: DerivedSurface,
  isPage: boolean,
  textOnBackground: boolean,
): State {
  const state = record(value);
  const shows = (fill: Paint | undefined, behind: string, level: number) =>
    fill && (isPage || contrast(fill.hex, behind) >= level) ? fill : undefined;
  const bg = shows(
    paint(colours, state.bg),
    surface.bg,
    colours.levels.nonText,
  );
  const fg = paint(colours, state.fg);
  return {
    bg,
    fg: textOnBackground
      ? shows(fg, bg?.hex ?? surface.bg, colours.levels.text)
      : fg,
  };
}

/**
 * Sekundær knap on one background: `globals['button-secondary']`, else the Tekst palette colour, where it stands out
 * from that background (on the page always), else the background's own text colour; `buttons.secondary` gives its
 * pattern, and its exact states wherever they stand out, whichever colour the button takes there.
 */
function secondaryVars(
  colours: SchemeColours,
  surface: DerivedSurface,
  isPage: boolean,
): Vars {
  const choice = record(colours.buttons.secondary);
  const outline = choice.pattern === "outline";
  const chosen =
    paint(colours, colours.globals["button-secondary"]) ??
    rolePaint(colours, "text");
  const level = outline ? colours.levels.text : colours.levels.nonText;
  const own =
    chosen && (isPage || contrast(chosen.hex, surface.bg) >= level)
      ? chosen
      : undefined;
  const fill = own ?? { css: surface.text, hex: surface.text };
  const label = own ? readableOn(colours, own, surface) : surface.bg;
  const lighter =
    choice.pattern === "lighter" ||
    (choice.pattern !== "darker" && lighterAway(fill.hex, label));
  const hover = exactState(colours, choice.hover, surface, isPage, false);
  const active = exactState(colours, choice.active, surface, isPage, false);
  const textOn = (state: State, otherwise: string) =>
    state.fg?.css ??
    (state.bg ? readableOn(colours, state.bg, surface) : otherwise);
  const hoverBg =
    hover.bg?.css ?? (outline ? fill.css : toneOf(fill.css, lighter, 1));
  const hoverText = textOn(hover, label);
  return {
    "re-button-secondary-bg": outline ? "transparent" : fill.css,
    "re-button-secondary-text": outline ? fill.css : label,
    "re-button-secondary-border": fill.css,
    "re-button-secondary-hover-bg": hoverBg,
    "re-button-secondary-hover-text": hoverText,
    "re-button-secondary-active-bg":
      active.bg?.css ??
      hover.bg?.css ??
      toneOf(fill.css, lighter, outline ? 1 : 2),
    "re-button-secondary-active-text": textOn(active, hoverText),
  };
}

/** Tekstlink's exact states on one background; `initial` leaves the section's own link colour and pattern. */
function textLinkVars(
  colours: SchemeColours,
  surface: DerivedSurface,
  isPage: boolean,
): Vars {
  const choice = record(colours.buttons.link);
  const hover = exactState(colours, choice.hover, surface, isPage, true);
  const active = exactState(colours, choice.active, surface, isPage, true);
  const textOn = (state: State, otherwise: string) =>
    state.fg?.css ??
    (state.bg ? readableOn(colours, state.bg, surface) : otherwise);
  const hoverText = textOn(hover, "initial");
  const hoverBg = hover.bg?.css ?? "initial";
  return {
    "re-text-link-hover-text": hoverText,
    "re-text-link-hover-bg": hoverBg,
    "re-text-link-active-text": textOn(active, hoverText),
    "re-text-link-active-bg": active.bg?.css ?? hoverBg,
  };
}

/** The gen-2 sections' Sekundær knap and Tekstlink colours on one background; the same names on every background. */
export function buttonTypeVars(
  colours: SchemeColours,
  surface: DerivedSurface,
  isPage: boolean,
): Vars {
  return {
    ...secondaryVars(colours, surface, isPage),
    ...textLinkVars(colours, surface, isPage),
  };
}

/** Tekstlink's pattern: Mørkere and Lysere tone the link's own colour on hover instead of underlining it. */
export function textLinkTone(theme: unknown): "darker" | "lighter" | undefined {
  const pattern = record(
    record(record(record(theme).colorSystem).buttons).link,
  ).pattern;
  return pattern === "darker" || pattern === "lighter" ? pattern : undefined;
}

/** Colours derived v2 stores beside the roles, with the older variables they stand for. */
const GLOBAL_COMPANIONS: Record<string, string[]> = {
  "choice-text": ["--checkbox-text-color"],
};

/** The older variables of each button derived v2 stores among the global colours. */
const GLOBAL_BUTTONS = {
  form: "form-button",
  search: "search-button",
  submenu: "casedetails-submenu",
} as const;

/**
 * Derived v2's global colours of one scheme (`derived.globals.<scheme>`) as stored: each `GLOBAL_COLOURS` role's
 * `--re-<role>` and the older variables it stands for, and the form, search and case submenu buttons with every state.
 */
export function globalsVars(globals: unknown): Vars {
  const vars: Vars = {};
  const colours = record(record(globals).colours);
  for (const { role, legacy } of [
    ...GLOBAL_COLOURS,
    ...Object.entries(GLOBAL_COMPANIONS).map(([role, legacy]) => ({
      role,
      legacy,
    })),
  ]) {
    const value = colours[role];
    if (!isColour(value)) continue;
    vars[`re-${role}`] = value;
    for (const name of legacy ?? []) vars[name.slice(2)] = value;
  }
  const buttons = record(record(globals).buttons);
  for (const [kind, prefix] of Object.entries(GLOBAL_BUTTONS)) {
    const button = derivedButton(buttons[kind]);
    if (!button) continue;
    vars[`${prefix}-bg-color`] = button.bg;
    vars[`${prefix}-text-color`] = button.fg;
    vars[`${prefix}-border-color`] = button.border;
    vars[`${prefix}-hover-bg-color`] = button.hover.bg;
    vars[`${prefix}-hover-text-color`] = button.hover.fg;
    vars[`${prefix}-active-bg-color`] = button.active.bg;
    vars[`${prefix}-active-text-color`] = button.active.fg;
  }
  return vars;
}

/** Derived v2's Sekundær knap (`btn2`) and Tekstlink states (`linkHover`, `linkActive`) of one surface, as stored. */
export function lookButtonTypeVarsV2(surface: DerivedSurface): Vars {
  const vars: Vars = {};
  const { btn2, linkHover, linkActive } = surface;
  if (btn2) {
    vars["re-button-secondary-bg"] = btn2.bg;
    vars["re-button-secondary-text"] = btn2.fg;
    vars["re-button-secondary-border"] = btn2.border;
    vars["re-button-secondary-hover-bg"] = btn2.hover.bg;
    vars["re-button-secondary-hover-text"] = btn2.hover.fg;
    vars["re-button-secondary-active-bg"] = btn2.active.bg;
    vars["re-button-secondary-active-text"] = btn2.active.fg;
  }
  for (const [state, pair] of [
    ["hover", linkHover],
    ["active", linkActive],
  ] as const)
    if (pair) {
      vars[`re-text-link-${state}-text`] = pair.fg;
      vars[`re-text-link-${state}-bg`] = pair.bg;
    }
  return vars;
}
